// G.4 — Recurring freight scheduler API hooks.
// Auth headers are attached globally by the axios interceptor in AuthContext.
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useRecurringTemplates = () =>
  useQuery({
    queryKey: ["recurring-templates"],
    queryFn: () => axios.get(`${API}/recurring-freight/templates`).then((r) => r.data),
  });

export const useRecurringTemplate = (tid) =>
  useQuery({
    queryKey: ["recurring-template", tid],
    queryFn: () => axios.get(`${API}/recurring-freight/templates/${tid}`).then((r) => r.data),
    enabled: !!tid,
  });

export const useRecurringChildren = (tid) =>
  useQuery({
    queryKey: ["recurring-children", tid],
    queryFn: () => axios.get(`${API}/recurring-freight/templates/${tid}/children`).then((r) => r.data),
    enabled: !!tid,
  });

export const useCreateTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => axios.post(`${API}/recurring-freight/templates`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recurring-templates"] }),
  });
};

export const usePatchTemplate = (tid) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch) => axios.patch(`${API}/recurring-freight/templates/${tid}`, patch).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-templates"] });
      qc.invalidateQueries({ queryKey: ["recurring-template", tid] });
    },
  });
};

export const useDeleteTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tid) => axios.delete(`${API}/recurring-freight/templates/${tid}`).then(() => tid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recurring-templates"] }),
  });
};

export const useCreateSchedule = (tid) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => axios.post(`${API}/recurring-freight/templates/${tid}/schedules`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-template", tid] });
      qc.invalidateQueries({ queryKey: ["recurring-templates"] });
    },
  });
};

export const usePatchSchedule = (tid) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sid, patch }) => axios.patch(`${API}/recurring-freight/schedules/${sid}`, patch).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-template", tid] });
      qc.invalidateQueries({ queryKey: ["recurring-templates"] });
    },
  });
};

export const useDeleteSchedule = (tid) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sid) => axios.delete(`${API}/recurring-freight/schedules/${sid}`).then(() => sid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-template", tid] });
      qc.invalidateQueries({ queryKey: ["recurring-templates"] });
    },
  });
};

export const useTickSchedule = (tid) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sid) => axios.post(`${API}/recurring-freight/schedules/${sid}/tick`, {}).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recurring-template", tid] });
      qc.invalidateQueries({ queryKey: ["recurring-children", tid] });
      qc.invalidateQueries({ queryKey: ["recurring-templates"] });
    },
  });
};
